
export enum UserRole {
  USER = 'USER',
  ADMIN = 'ADMIN'
}

export enum UserTier {
  FREE = 'FREE',
  PREMIUM = 'PREMIUM'
}

export type ThemeMode = 'light' | 'dark';

export type SubscriptionStatus = 'ACTIVE' | 'TRIAL' | 'EXPIRED' | 'NONE';

export interface NotificationSettings {
  pushEnabled: boolean;
  quietHoursEnabled: boolean;
  quietHoursStart: string; // "22:00"
  quietHoursEnd: string; // "08:00"
  notifyOn: SystemEvent[];
}

export interface UserSettings {
  theme: ThemeMode;
  calendarSynced: boolean;
  googleCalendarId?: string;
  lastCalendarSync?: number;
  notifications?: NotificationSettings;
}

export interface User {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
  tier: UserTier;
  createdAt: number;
  lastLogin: number;
  premiumExpiry?: number;
  subscriptionStatus: SubscriptionStatus;
  missionCount?: number;
  settings?: UserSettings;
  stripeId?: string;
  stripeLink?: string;
  isActive?: boolean;
  pushToken?: string;
  timezone?: string;
  timezoneOffset?: number;
}

export interface PostmarkConfig {
  serverToken: string;
  fromEmail: string;
  lastUpdated?: number;
  updatedBy?: string;
}

export interface SystemConfig {
  stripePublicKey: string;
  stripeSecretKey: string;
  stripeWebhookSecret: string;
  monthlyPriceCents?: number;
  yearlyPriceCents?: number;
  lastUpdated?: number;
  updatedBy?: string;
}

export type BroadcastGroup = 'ALL' | 'PREMIUM' | 'FREE' | 'ADMINS' | 'INDIVIDUAL';
export type DeliveryChannel = 'IN_APP' | 'EMAIL';

export interface AudienceFilter {
  type: 'ALL' | 'ACTIVE_RECENTLY' | 'NEW_USERS' | 'NEVER_LOGGED_IN';
  days?: number;
}

export interface Broadcast {
  id: string;
  title: string;
  message: string;
  targetGroup: BroadcastGroup;
  targetUserId?: string; // For INDIVIDUAL targeting
  channels?: DeliveryChannel[]; // New: Delivery vectors
  audienceFilter?: AudienceFilter; // New: Segmentation logic
  timestamp: number;
  senderName: string;
  priority: 'NORMAL' | 'CRITICAL';
  ctaLabel?: string;
  ctaUrl?: string;
}

export interface UserNotification {
  id: string;
  userId: string;
  broadcastId: string;
  read: boolean;
  receivedAt: number;
}

export type FeedbackType = 'BUG' | 'PERFORMANCE' | 'FEATURE';
export type FeedbackStatus = 'NEW' | 'IN_PROGRESS' | 'COMPLETED' | 'WONT_FIX';

export interface Feedback {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  type: FeedbackType;
  message: string;
  status: FeedbackStatus;
  adminNotes?: string;
  createdAt: number;
  updatedAt: number;
}

export enum Quadrant {
  DO = 'Q1',
  SCHEDULE = 'Q2',
  DELEGATE = 'Q3',
  ELIMINATE = 'Q4'
}

export type TaskStatus = 'NEW' | 'IN_PROGRESS' | 'COMPLETED' | 'ARCHIVED';

export interface Category {
  id: string;
  userId: string;
  name: string;
  color: string;
  isDefault?: boolean;
}

export interface SubTask {
  id: string;
  text: string;
  completed: boolean;
  promoted?: boolean;
  promotedTaskId?: string;
  promotedAt?: number;
}

export type SystemEvent =
  | 'created' | 'completed' | 'subtask' | 'daily' | 'weekly' | 'system_trace'
  | 'welcome_new_user' | 'profile_deleted' | 'partner_update' | 'system_check'
  | 'password_reset_requested'
  | 'nudge_urgent'
  | 'nudge_strategic'
  | 'partner_request'
  | 'inactivity_7_days'
  | 'streak_7_day'
  | 'broadcast';

export interface HopTrace {
  step: number;
  name: string;
  status: 'PASS' | 'FAIL' | 'PENDING' | 'TIMEOUT';
  timestamp: number;
  details: string;
}

export type AssociationSchedule = 'realtime' | 'daily' | 'weekly';
export type AssociationDeliveryMode = 'per_user' | 'broadcast';

export interface TriggerAssociation {
  id: string;
  systemEvent: SystemEvent;
  emailTask: string;
  description?: string;
  schedule?: AssociationSchedule;
  deliveryMode?: AssociationDeliveryMode;
}

export interface AccountabilityPartner {
  email: string;
  notifyOn: SystemEvent[];
}

export interface QuadrantMovement {
  fromQuadrant: Quadrant;
  toQuadrant: Quadrant;
  movedAt: number;
}

export interface Task {
  id: string;
  taskId: string;
  userId: string;
  title: string;
  description: string | null;
  initialQuadrant: Quadrant;
  currentQuadrant: Quadrant;
  quadrantHistory: QuadrantMovement[];
  status: TaskStatus;
  hasSubtasks: boolean;
  subtaskCount: number;
  completedSubtaskCount: number;
  subtasks: SubTask[];
  createdAt: number;
  updatedAt: number;
  completedAt: number | null;
  dueDate?: number;
  category: string;
  tags: string[];
  completionType?: 'MANUAL' | 'ALL_SUBTASKS_COMPLETED';
  source?: 'USER_CREATED' | 'AI_SUGGESTED' | 'SYSTEM_IMPORTED';
  partners: AccountabilityPartner[];
  isCompleted: boolean;
  originType?: 'task' | 'promoted_subtask';
  parentTaskId?: string;
  parentSubtaskId?: string;
  promotion?: {
    promotedAt: number;
    promotedBy: string;
  };
  lastNudgedAt?: number;
}

export interface EmailLog {
  id: string;
  channel: 'EMAIL' | 'IN_APP';
  triggerType: string;
  recipient: string;
  status: 'SENT' | 'FAILED' | 'PENDING';
  timestamp: number;
  diagnostic?: string;
  subject?: string;
  messageId?: string;
  metadata?: any;
}

export interface MessageTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  lastModified?: number;
}

// Trust CMS Types
export type TrustCategory = 'PRIVACY' | 'SECURITY' | 'LEGAL' | 'COMPLIANCE' | 'OTHER';

export interface Actor {
  uid: string;
  email: string;
}

export interface TrustDocument {
  id: string;
  slug: string;
  title: string;
  category: TrustCategory;
  status: 'DRAFT' | 'PUBLISHED';
  currentVersionId: string | null;
  publishedVersionId: string | null;
  createdAt: number;
  createdBy: Actor;
  updatedAt: number;
  updatedBy: Actor;
  lastPublishedAt: number | null;
  lastPublishedBy: Actor | null;
}

export interface TrustVersion {
  versionId: string;
  versionNumber: number;
  contentFormat: 'MARKDOWN';
  content: string;
  effectiveDate: number | null;
  createdAt: number;
  createdBy: Actor;
  changeSummary: string;
  isPublished: boolean;
}

export interface TrustVersionAuditLog {
  id: string;
  docId: string;
  versionId: string;
  action: 'PUBLISH' | 'CREATE' | 'UNPUBLISH';
  actor: Actor;
  timestamp: number;
  details: any;
}

export interface TrustAuditLog {
  id: string;
  docId: string;
  versionId: string;
  action: 'PUBLISH' | 'CREATE' | 'UNPUBLISH';
  actor: Actor;
  timestamp: number;
  details: any;
}

export interface SocialLink {
  id: string;
  platform: 'instagram' | 'facebook' | 'youtube' | 'x' | 'linkedin' | 'github' | 'tiktok' | 'other';
  label: string;
  url: string;
  isActive: boolean;
  sortOrder: number;
}

export interface AnalyticsMetrics {
  prioriScore: number;
  deepWorkRatio: number;
  questVelocity: number;
  momentumConsistency: number;
  timeToCompletion: number;
  planningAccuracy: number;
  focusLeverage: number;
  activeUsers: number;
  churnRate: number;
  ltv: number;
  cac: number;
  mrr: number;
  arr: number;
  dau: number;
  mau: number;
  stickiness: number;
  conversionRate: number;
  leaks: number;
  burnChurnSignal: 'STABLE' | 'WARNING' | 'CRITICAL';
  featurePenetration: Record<string, number>;
  burnRate: number;
  ltvCacRatio: number;
  systemHealth: number;
  latency: number;
  burnMultiplier: number;
  paybackMonths: number;
  ruleOf40: number;
  errorRate: number;
  growthVelocity: number;
}

export type MetricKey = 'prioriScore' | 'questVelocity' | 'deepWorkRatio';

export interface DailySnapshot {
  date: string;
  timestamp: number;
  value: number;
}